import React, { useState, useMemo, useCallback } from "react";
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup,
} from "react-simple-maps";
import "./WorldOnboardingMap.css";

// ─── TopoJSON source ──────────────────────────────────────────────────────────
const GEO_URL =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ─── Geography style objects (react-simple-maps API, module-level constants) ──
const GEO_STYLE_DEFAULT = {
    default: {
        fill: "#E2E8F0",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#CBD5E1",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#CBD5E1", outline: "none" },
};

const GEO_STYLE_ONBOARDED = {
    default: {
        fill: "#EF7D32",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        transition: "fill 0.2s ease",
    },
    hover: {
        fill: "#C44E00",
        stroke: "#ffffff",
        strokeWidth: 0.5,
        outline: "none",
        cursor: "pointer",
    },
    pressed: { fill: "#C44E00", outline: "none" },
};

// ─── Component ────────────────────────────────────────────────────────────────
// onboardedCountries: [{ name: string }]
// Matching is done against geo.properties.name from the TopoJSON — fully
// dynamic, no hardcoded ISO tables required. Any country name from the API
// will be reflected automatically as long as it matches the TopoJSON name.
const WorldOnboardingMap = ({ onboardedCountries = [] }) => {
    const [tooltip, setTooltip] = useState({
        visible: false,
        x: 0,
        y: 0,
        name: "",
        isOnboarded: false,
    });

    // Normalised Set of country names from the API — O(1) lookup
    const onboardedNameSet = useMemo(
        () =>
            new Set(
                onboardedCountries
                    .map((c) => (c.name || "").toLowerCase().trim())
                    .filter(Boolean)
            ),
        [onboardedCountries]
    );

    const handleMouseEnter = useCallback(
        (geo, evt) => {
            const geoName = geo.properties?.name || "";
            const isOnboarded = onboardedNameSet.has(geoName.toLowerCase().trim());
            const rect =
                evt.currentTarget
                    .closest(".wom-map-wrapper")
                    ?.getBoundingClientRect() || { left: 0, top: 0 };
            setTooltip({
                visible: true,
                x: evt.clientX - rect.left,
                y: evt.clientY - rect.top,
                name: geoName || "Unknown",
                isOnboarded,
            });
        },
        [onboardedNameSet]
    );

    const handleMouseMove = useCallback((evt) => {
        const rect =
            evt.currentTarget
                .closest(".wom-map-wrapper")
                ?.getBoundingClientRect() || { left: 0, top: 0 };
        setTooltip((prev) => ({
            ...prev,
            x: evt.clientX - rect.left,
            y: evt.clientY - rect.top,
        }));
    }, []);

    const handleMouseLeave = useCallback(() => {
        setTooltip((prev) => ({ ...prev, visible: false }));
    }, []);

    return (
        <div className="wom-container">
            {/* ── Header ── */}
            <div className="wom-header">
                <div className="wom-header-left">
                    <h2 className="wom-title">Live Markets</h2>
                </div>
                <div className="wom-badge">
                    <span className="wom-badge-dot" />
                    <span className="wom-badge-text">
                        {onboardedCountries.length}{" "}
                        {onboardedCountries.length === 1 ? "Country" : "Countries"}{" "}
                        Onboarded
                    </span>
                </div>
            </div>

            {/* ── Map ── */}
            <div className="wom-map-wrapper">
                <ComposableMap
                    projectionConfig={{ scale: 320, center: [10, 15] }}
                    className="wom-composable-map"
                >
                    <ZoomableGroup zoom={1} minZoom={0.6} maxZoom={10}>
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map((geo) => {
                                    const geoName = (geo.properties?.name || "").toLowerCase().trim();
                                    const isOnboarded = onboardedNameSet.has(geoName);
                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onMouseEnter={(evt) =>
                                                handleMouseEnter(geo, evt)
                                            }
                                            onMouseMove={handleMouseMove}
                                            onMouseLeave={handleMouseLeave}
                                            style={
                                                isOnboarded
                                                    ? GEO_STYLE_ONBOARDED
                                                    : GEO_STYLE_DEFAULT
                                            }
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ZoomableGroup>
                </ComposableMap>

                {/* ── Tooltip ── */}
                {tooltip.visible && (
                    <div
                        className={`wom-tooltip ${tooltip.isOnboarded
                            ? "wom-tooltip--onboarded"
                            : "wom-tooltip--default"
                            }`}
                        style={{
                            "--wom-x": `${tooltip.x + 14}px`,
                            "--wom-y": `${tooltip.y - 10}px`,
                        }}
                    >
                        <span className="wom-tooltip-name">{tooltip.name}</span>
                        <span
                            className={`wom-tooltip-status ${tooltip.isOnboarded
                                ? "wom-tooltip-status--on"
                                : "wom-tooltip-status--off"
                                }`}
                        >
                            {tooltip.isOnboarded ? "✓ Onboarded" : "Not Onboarded"}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Legend ── */}
            <div className="wom-legend">
                <div className="wom-legend-item">
                    <span className="wom-legend-dot wom-legend-dot--onboarded" />
                    <span className="wom-legend-label">Onboarded</span>
                </div>
                <div className="wom-legend-hint">Scroll to zoom · Drag to pan</div>
            </div>
        </div>
    );
};

export default WorldOnboardingMap;
